import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { supabase } from '@/lib/supabaseClient';
import type { Article } from '@/types/article';

export default function ArticlesSection() {
  const [articles, setArticles] = useState<Article[]>([]);
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';

  useEffect(() => {
    supabase
      .from('rxk_articles')
      .select('id, slug, category, category_color, read_time, title, excerpt, author, author_role, published_date, image, body, status')
      .eq('status', 'published')
      .order('published_date', { ascending: false })
      .limit(3)
      .then(({ data }) => setArticles((data as Article[]) ?? []));
  }, []);

  if (articles.length === 0) return null;

  return (
    <section className="w-full bg-[#F0EFED]/50 py-20 md:py-28 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="flex flex-col gap-4 mb-12 md:mb-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-px bg-[#7B1113]" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
              {t('home.articles.label')}
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#2A2A2A] leading-[1.15]">
            {t('home.articles.heading')}
          </h2>
        </div>

        {/* Article cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={`${prefix}/content/${article.slug}`}
              className="group flex flex-col bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            >
              {/* Image */}
              <div className="relative w-full h-48 overflow-hidden">
                <img
                  src={article.image ?? ''}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span
                    className="text-[9px] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full text-white font-medium"
                    style={{ backgroundColor: article.category_color }}
                  >
                    {article.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-col gap-3 p-6">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#8A8A8A] font-light">
                    {article.read_time}
                  </span>
                </div>

                <h3 className="font-serif text-lg font-semibold text-[#2A2A2A] leading-tight group-hover:text-[#7B1113] transition-colors">
                  {article.title}
                </h3>

                <p className="text-xs text-[#8A8A8A] font-light leading-[1.7] line-clamp-2">
                  {article.excerpt}
                </p>

                <div className="flex items-center gap-2 mt-1 pt-3 border-t border-[#F0EFED]">
                  <span className="text-[10px] text-[#2A2A2A] font-medium">
                    {article.author}
                  </span>
                  <span className="text-[#8A8A8A]/40">&middot;</span>
                  <span className="text-[10px] text-[#8A8A8A] font-light">
                    {article.published_date}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="flex justify-center mt-12">
          <Link
            to={prefix + '/content'}
            className="w-full sm:w-auto px-8 py-3.5 border border-[#2A2A2A]/20 text-[#2A2A2A] text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#2A2A2A] hover:text-white transition-all cursor-pointer text-center"
          >
            {t('home.articles.cta')}
          </Link>
        </div>
      </div>
    </section>
  );
}